import { dayjs } from '../support/helpers/dayjs.ts';
import { App } from '@capacitor/app';
import { IAuthorityErrata, CachedErrataProvider } from '@autopylot-internal/tiles-client';
import { State } from '@autopylot-internal/state';
import { Feature } from 'geojson';
import { IAuthorizableGeometry, IAuthorizableProperties } from '@autopylot-internal/tiles-client';
import { Logger } from '../support/debug/Logger.ts';

// Check for errata changes every minute. This does not make network requests —
// it reads from CachedErrataProvider's cache. CachedErrataProvider handles the
// actual HTTP polling independently.
const POLL_EVERY_MS = 1 * 60 * 1000; // 1 min

// how long to allow errata to fail before logging error to sentry
const LOG_ERROR_AFTER_MS = 10 * 60 * 1000; // 10 mins

export class ErrataState extends State<{}> {
    static inject = () => [CachedErrataProvider, Logger];
    constructor(private errataProvider: CachedErrataProvider, private log: Logger){
        super({});
        this.resetTimestamps();
    }

    private currentErrata: IAuthorityErrata | undefined;

    private errataTimestamp = Date.now(); // when we last successfully retrieved errata
    private logTimestamp = Date.now(); // when we last logged an error about failed errata

    private resetTimestamps = () => {
        this.errataTimestamp = Date.now();
        this.logTimestamp = Date.now();
    };

    private syncLocalErrataFromRemote = async () => {
        const { isActive } = await App.getState();
        if (!isActive) {
            this.errataProvider.disablePolling();
            this.resetTimestamps();
            return;
        }

        await this.errataProvider.enablePolling();

        const errata = await this.errataProvider.getErrata();

        const lastSuccessAt = this.errataProvider.getLastSuccessAt();
        if (lastSuccessAt) {
            this.errataTimestamp = lastSuccessAt;
        }

        if (Date.now() - this.errataTimestamp > LOG_ERROR_AFTER_MS) {
            if (Date.now() - this.logTimestamp > LOG_ERROR_AFTER_MS) {
                const errataAge = dayjs().diff(dayjs(this.errataTimestamp), 'm');
                const wrappedError = new Error(`errata has not been successfully fetched for ${errataAge} mins`);
                this.log.error(wrappedError);
                this.logTimestamp = Date.now();
            }
        }

        if (!this.currentErrata || JSON.stringify(this.currentErrata) !== JSON.stringify(errata)){
            this.currentErrata = errata;
            this.setState({});
        }
    };

    pollForErrata = async () => {
        await this.errataProvider.enablePolling();
        while (true){
            await this.syncLocalErrataFromRemote();
            await new Promise(resolve => setTimeout(resolve, POLL_EVERY_MS));
        }
    };

    getRemoveIds = (): string[] => {
        if (!this.currentErrata){
            return [];
        }
        return this.currentErrata.removes.map(r => r.GLOBAL_ID).concat(this.currentErrata.changes.map(f => f.properties!.GLOBAL_ID));
    };

    getErrataFeatures = (): Feature<IAuthorizableGeometry, IAuthorizableProperties>[] => {
        if (!this.currentErrata){
            return [];
        }
        return this.currentErrata.adds.concat(this.currentErrata.changes);
    };
}
